import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Save, EyeOff, Eye, ChevronUp, ChevronDown } from "lucide-react";

/**
 * Super-Admin panel: toggle which sidebar items each role can see. The
 * result is persisted as `platform_config.nav_visibility` and consumed
 * by `DashboardLayout` at render time.
 *
 * Roles enumerated here match `getNavForRole` in DashboardLayout.jsx.
 * Items enumerated here match every URL the sidebar ever exposes — kept
 * in sync manually since we don't want the layout file to become a source
 * of truth for admin config (chicken-and-egg).
 */
const ROLES = [
  { key: "admin", label: "Admin" },
  { key: "super_admin", label: "Super Admin" },
  { key: "employee", label: "Employee" },
  { key: "intern", label: "Intern" },
  { key: "mentor", label: "Mentor" },
  { key: "tutor", label: "Tutor" },
  { key: "partner_institution", label: "Partner Institution" },
  { key: "student", label: "Student" },
  { key: "client", label: "Client" },
];

// (label, key) — keys can be a URL for items or `group:<Label>` for groups.
// IMPORTANT: keys here MUST match the `to` on the actual nav item in
// DashboardLayout for the hide toggle to have any effect.
const ALL_ITEMS = [
  { section: "Client / Student", items: [
    ["Overview", "/dashboard"],
    ["Announcements", "/dashboard/announcements"],
    ["Chat", "/dashboard/chat"],
    ["Task Board", "/dashboard/employee/kanban"],
    ["Assigned Projects", "/dashboard/employee/assigned-projects"],
    ["Ongoing Research", "/dashboard/ideas"],
    ["Custom Guidance", "/dashboard/guidance"],
    ["Mentors", "/dashboard/mentors"],
    ["Journals & Conferences", "/dashboard/journals"],
    ["Learn (LMS)", "/dashboard/courses"],
    ["Surveys", "/dashboard/surveys"],
    ["Portfolio", "/dashboard/portfolio"],
    ["Plagiarism Check", "/dashboard/plagiarism"],
    ["Submit Project / Thesis", "/dashboard/student-upload"],
  ]},
  { section: "Employee / Intern", items: [
    ["Assign Tasks", "/dashboard/assign-tasks"],
    ["My Teams", "/dashboard/employee/teams"],
    ["Project Teams", "/admin/teams"],
    ["Direct Reports", "/dashboard/employee/direct-reports"],
    ["Work Submissions", "/dashboard/employee/submissions"],
    ["My Ideas", "/dashboard/ideas"],
    ["Daily Log", "/dashboard/intern/daily-log"],
    ["Meetings", "/dashboard/employee/meetings"],
    ["ERP (group)", "group:ERP"],
    ["Attendance", "/dashboard/employee/attendance"],
    ["Payslips", "/dashboard/employee/payslips"],
    ["Leaves", "/dashboard/employee/erp/leaves"],
    ["Documents", "/dashboard/employee/erp/documents"],
    ["Expenses", "/dashboard/employee/erp/expenses"],
    ["Performance", "/dashboard/employee/erp/performance"],
    ["Training", "/dashboard/employee/erp/training"],
  ]},
  { section: "Mentor / Tutor", items: [
    ["Mentor Overview", "/mentor"],
    ["Incoming Requests", "/mentor/requests"],
    ["Mentor Projects", "/mentor/projects"],
    ["Course Earnings", "/mentor/earnings"],
    ["My Courses", "/dashboard/courses?tab=mine"],
    ["My Portfolio", "/dashboard/portfolio"],
  ]},
  { section: "Admin", items: [
    ["Admin Overview", "/admin"],
    ["Users & Roles", "/admin/users"],
    ["Role Approvals", "/admin/role-approvals"],
    ["Mentor Requests", "/admin/mentor-requests"],
    ["Careers / Vacancies", "/admin/vacancies"],
    ["Project Teams (admin)", "/admin/teams"],
    ["Org Units", "/admin/org"],
    ["Assign Tasks (admin)", "/dashboard/assign-tasks"],
    ["Employee Analytics", "/admin/interns"],
    ["Pending Ideas", "/admin/pending-ideas"],
    ["All Projects", "/admin/projects"],
    ["Published Papers", "/admin/papers"],
    ["Guidance Queue", "/admin/guidance"],
    ["Course Review", "/admin/course-review"],
    ["Tutorials", "/admin/tutorials"],
    ["Research Ideas (premium)", "/admin/research-ideas"],
    ["Datasets", "/admin/datasets"],
    ["Paper Explainers", "/admin/explainers"],
    ["Journals & Conferences (admin)", "/admin/journals"],
    ["Blog / Journal Posts", "/admin/blogs"],
    ["Landing Roadmap", "/admin/landing-journey"],
    ["Taxonomy", "/admin/taxonomy"],
    ["Events", "/admin/events"],
    ["Payments", "/admin/payments"],
    ["Pricing & Coupons", "/admin/pricing"],
    ["Subscription Plans", "/admin/plans"],
    ["Site Analytics", "/admin/analytics"],
    ["Media Manager", "/admin/media"],
    ["Platform Config", "/admin/config"],
    ["Mailbox", "/admin/mailbox"],
    ["Analytics & Emails", "/admin/email-engine"],
  ]},
];

export default function AdminNavVisibility() {
  const [visibility, setVisibility] = useState({}); // role -> [hidden keys]
  const [order, setOrder] = useState({}); // role -> [ordered keys]
  const [role, setRole] = useState(ROLES[0].key);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/config/nav-visibility").catch(() => ({ data: {} })),
      api.get("/config/nav-order").catch(() => ({ data: {} })),
    ])
      .then(([v, o]) => { setVisibility(v.data || {}); setOrder(o.data || {}); })
      .catch(() => toast.error("Couldn't load nav configuration"))
      .finally(() => setLoading(false));
  }, []);

  const hidden = useMemo(() => new Set(visibility[role] || []), [visibility, role]);
  const toggle = (key) => {
    setVisibility((v) => {
      const next = new Set(v[role] || []);
      if (next.has(key)) next.delete(key); else next.add(key);
      return { ...v, [role]: Array.from(next) };
    });
  };

  // Build an "ordered" flat list of keys the user cares about for the
  // currently-selected role, so we can render up/down arrows. Falls back to
  // ALL_ITEMS insertion order the first time the admin ever touches this role.
  const flatKeys = useMemo(() => {
    const inSections = [];
    for (const s of ALL_ITEMS) for (const [, k] of s.items) inSections.push(k);
    const saved = order[role] || [];
    const seen = new Set();
    const out = [];
    for (const k of saved) { if (inSections.includes(k) && !seen.has(k)) { out.push(k); seen.add(k); } }
    for (const k of inSections) if (!seen.has(k)) out.push(k);
    return out;
  }, [order, role]);

  const move = (key, dir) => {
    setOrder((o) => {
      const cur = [...flatKeys];
      const idx = cur.indexOf(key);
      if (idx < 0) return o;
      const swap = dir === "up" ? idx - 1 : idx + 1;
      if (swap < 0 || swap >= cur.length) return o;
      [cur[idx], cur[swap]] = [cur[swap], cur[idx]];
      return { ...o, [role]: cur };
    });
  };

  const save = async () => {
    setBusy(true);
    try {
      await api.put("/admin/config/nav-visibility", { visibility });
      await api.put("/admin/config/nav-order", { order });
      toast.success("Nav visibility & order saved");
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  return (
    <div data-testid="admin-nav-visibility-page">
      <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted">SUPER ADMIN · UI</div>
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl text-kb-ink mt-1">Sidebar <em className="italic text-kb-primary">visibility</em></h1>
          <p className="text-sm text-kb-ink-muted mt-2 max-w-2xl">Toggle which sidebar items each role sees. Anything you hide here will be dropped from the sidebar for every user who holds the selected role — no code change required.</p>
        </div>
        <button onClick={save} disabled={busy} className="kb-btn-primary text-sm inline-flex items-center gap-1" data-testid="admin-nav-vis-save">
          <Save className="w-4 h-4" /> {busy ? "Saving…" : "Save"}
        </button>
      </div>

      {/* Role tabs */}
      <div className="mt-6 flex flex-wrap gap-1.5 border-b border-slate-200 pb-3">
        {ROLES.map((r) => (
          <button
            key={r.key}
            type="button"
            onClick={() => setRole(r.key)}
            className={`kb-mono text-[10px] uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${
              role === r.key ? "border-kb-primary bg-kb-primary text-white" : "border-slate-200 text-kb-ink-muted hover:bg-slate-50"
            }`}
            data-testid={`admin-nav-vis-role-${r.key}`}
          >
            {r.label}
            {(visibility[r.key] || []).length > 0 && (
              <span className="ml-1.5 kb-mono text-[9px] bg-white/30 text-current px-1 rounded-sm">{(visibility[r.key] || []).length} hidden</span>
            )}
          </button>
        ))}
      </div>

      {loading ? <div className="mt-8 kb-mono text-xs text-kb-ink-muted">Loading…</div> : (
        <div className="mt-6 space-y-6">
          {/* Section 1: sectional Show/Hide toggles */}
          {ALL_ITEMS.map((section) => (
            <div key={section.section}>
              <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">{section.section}</div>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-1.5">
                {section.items.map(([label, key]) => {
                  const isHidden = hidden.has(key);
                  return (
                    <label
                      key={key}
                      className={`text-left px-3 py-2 border rounded-sm text-xs inline-flex items-center gap-2 transition-colors cursor-pointer ${
                        isHidden
                          ? "border-rose-200 bg-rose-50 text-rose-700 hover:bg-rose-100"
                          : "border-slate-200 bg-white text-kb-ink hover:border-kb-primary/40"
                      }`}
                      data-testid={`admin-nav-vis-item-${key}`}
                    >
                      <input
                        type="checkbox"
                        checked={!isHidden}
                        onChange={() => toggle(key)}
                        className="accent-kb-primary shrink-0"
                        data-testid={`admin-nav-vis-check-${key}`}
                      />
                      <span className="truncate flex-1">{label}</span>
                      {isHidden ? <EyeOff className="w-3.5 h-3.5 shrink-0" /> : <Eye className="w-3.5 h-3.5 text-kb-ink-light shrink-0" />}
                    </label>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Section 2: ordering — a single ordered list per role. Move any
              item up / down; the sidebar will follow this order for users
              holding this role. Hidden items are dimmed but stay draggable
              so admins can pre-arrange them before unhiding. */}
          <div>
            <div className="kb-mono text-[10px] uppercase tracking-[0.2em] text-kb-ink-muted mb-2">
              Sidebar order · {ROLES.find((r) => r.key === role)?.label}
            </div>
            <div className="border border-slate-200 rounded-sm divide-y divide-slate-100 bg-white">
              {flatKeys.map((key, idx) => {
                const label = (() => {
                  for (const s of ALL_ITEMS) for (const [l, k] of s.items) if (k === key) return l;
                  return key;
                })();
                const isHidden = hidden.has(key);
                return (
                  <div key={key} className={`flex items-center gap-2 px-3 py-1.5 text-xs ${isHidden ? "opacity-50" : ""}`} data-testid={`admin-nav-order-${key}`}>
                    <span className="kb-mono text-[10px] text-kb-ink-muted w-6">{idx + 1}.</span>
                    <span className="flex-1 truncate">{label}</span>
                    <span className="kb-mono text-[9px] text-kb-ink-light truncate max-w-[160px]">{key}</span>
                    <button type="button" onClick={() => move(key, "up")} disabled={idx === 0}
                      className="p-1 text-kb-ink-muted hover:text-kb-primary disabled:opacity-30"
                      title="Move up" data-testid={`admin-nav-order-${key}-up`}>
                      <ChevronUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => move(key, "down")} disabled={idx === flatKeys.length - 1}
                      className="p-1 text-kb-ink-muted hover:text-kb-primary disabled:opacity-30"
                      title="Move down" data-testid={`admin-nav-order-${key}-down`}>
                      <ChevronDown className="w-3.5 h-3.5" />
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
